import React from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/context/I18nContext";
import { ArrowDown } from "lucide-react";

export default function Hero({ content }) {
    const { t, lang } = useI18n();

    const bg = content?.hero_background_url;
    const logo = content?.logo_url;
    const title = lang === "es" ? content?.hero_title_es : content?.hero_title_en;
    const subtitle = lang === "es" ? content?.hero_subtitle_es : content?.hero_subtitle_en;

    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative min-h-[100svh] w-full overflow-hidden bg-habana-ink"
        >
            {bg && (
                <div className="absolute inset-0">
                    <img
                        src={bg}
                        alt="Havana ambience"
                        className="w-full h-full object-cover object-center opacity-60"
                        loading="eager"
                        data-testid="hero-background"
                    />
                </div>
            )}
            <div className="absolute inset-0 hero-vignette" />
            {/* Gold radial accent */}
            <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-gold/20 blur-3xl" />
            <div className="absolute -bottom-40 -left-20 w-[520px] h-[520px] rounded-full bg-habana-red/25 blur-3xl" />

            <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-10 min-h-[100svh] grid grid-cols-1 md:grid-cols-12 items-center pt-28 pb-16 gap-10">
                {/* Left: dynamic logo */}
                <motion.div
                    className="md:col-span-5 flex items-center justify-center md:justify-start"
                    initial={{ opacity: 0, scale: 0.9, y: 30 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                >
                    {logo ? (
                        <div className="logo-halo animate-float-slow">
                            <img
                                src={logo}
                                alt="Cuba Bella Café Logo"
                                data-testid="hero-logo"
                                className="w-[280px] h-[280px] md:w-[420px] md:h-[420px] object-contain drop-shadow-[0_25px_60px_rgba(212,175,55,0.35)]"
                            />
                        </div>
                    ) : (
                        <div className="w-64 h-64 rounded-full bg-gold/10 border border-gold/30" />
                    )}
                </motion.div>

                {/* Right: title + CTAs */}
                <div className="md:col-span-7 max-w-2xl">
                    <motion.span
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.15 }}
                        className="ornament"
                        data-testid="hero-badge"
                    >
                        {t("badge_hero")}
                    </motion.span>

                    <motion.h1
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.0, delay: 0.25 }}
                        className="mt-4 font-serif text-5xl sm:text-6xl md:text-7xl leading-[1.05] tracking-tight text-habana-cream"
                        data-testid="hero-title"
                    >
                        {title?.split(" ").slice(0, -2).join(" ")}{" "}
                        <span className="italic gold-shimmer">
                            {title?.split(" ").slice(-2).join(" ")}
                        </span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.0, delay: 0.4 }}
                        className="mt-6 text-lg md:text-xl text-habana-cream/80 font-light leading-relaxed max-w-xl"
                        data-testid="hero-subtitle"
                    >
                        {subtitle}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.55 }}
                        className="mt-10 flex flex-wrap gap-4"
                    >
                        <a href="#menu" className="btn-gold" data-testid="hero-cta-menu">
                            {t("cta_view_menu")}
                        </a>
                        <a href="#contact" className="btn-ghost-gold" data-testid="hero-cta-reserve">
                            {t("cta_reserve")}
                        </a>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.2, duration: 0.8 }}
                        className="mt-10 font-script text-2xl text-gold"
                    >
                        “{t("hero_tagline")}”
                    </motion.p>
                </div>
            </div>

            <a
                href="#menu"
                aria-label="Scroll to menu"
                className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center text-gold/80 hover:text-gold transition-colors"
            >
                <span className="text-[0.7rem] tracking-[0.35em] uppercase mb-2">scroll</span>
                <ArrowDown size={20} className="animate-bounce" />
            </a>
        </section>
    );
}
